/**
 * Visit Notes Controller (Driver Adapter)
 *
 * Interface layer between UI and business logic
 */

import type {
  IVisitNotesController,
  IVisitNotesService,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  ICreateVisitNoteResponse,
  IUpdateVisitNoteResponse,
  IGetAllVisitNotesResponse,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  IGetSidePeekProfileResponse,
  IGenerateSidePeekProfileResponse,
  IUpdateSidePeekProfileResponse,
  IGetSafetyNoteResponse,
  IUpdateSafetyNoteResponse,
  IGetCodeStatusResponse,
  IUpdateCodeStatusResponse,
  CodeStatusValue,
} from "../../core/ports/driver/VisitNotesService";

export const VisitNotesControllerFactory = ({
  visitNotesService,
}: {
  visitNotesService: IVisitNotesService;
}): IVisitNotesController => {
  /**
   * Create a new visit note (draft or completed)
   */
  const createVisitNote = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteResponse> => {
    return await visitNotesService.createVisitNote(request);
  };

  /**
   * Update/auto-save a visit note
   */
  const updateVisitNote = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IUpdateVisitNoteResponse> => {
    return await visitNotesService.updateVisitNote(
      clinicId,
      patientId,
      noteId,
      request
    );
  };

  /**
   * Get all visit notes for a clinic
   */
  const getAllVisitNotes = async (
    clinicId: number
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.getAllVisitNotes(clinicId);
  };

  /**
   * Get a single visit note
   */
  const getVisitNote = async (
    noteId: number
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    return await visitNotesService.getVisitNote(noteId);
  };

  /**
   * Summarize a narrative for sharing as PDF
   */
  const summarizeNarrative = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    return await visitNotesService.summarizeNarrative(request);
  };

  /**
   * Send visit note PDF via eFax
   */
  const sendEfax = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    return await visitNotesService.sendEfax(request);
  };

  /**
   * Get Side Peek Profile for a patient
   */
  const getSidePeekProfile = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSidePeekProfileResponse> => {
    return await visitNotesService.getSidePeekProfile(clinicId, patientId);
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   */
  const generateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<IGenerateSidePeekProfileResponse> => {
    return await visitNotesService.generateSidePeekProfile(
      clinicId,
      patientId,
      noteId,
      section,
      model
    );
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   */
  const updateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
    }
  ): Promise<IUpdateSidePeekProfileResponse> => {
    return await visitNotesService.updateSidePeekProfile(
      clinicId,
      patientId,
      updateData
    );
  };

  /**
   * Get Safety Note for a patient
   */
  const getSafetyNote = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSafetyNoteResponse> => {
    return await visitNotesService.getSafetyNote(clinicId, patientId);
  };

  /**
   * Update Safety Note for a patient
   */
  const updateSafetyNote = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<IUpdateSafetyNoteResponse> => {
    return await visitNotesService.updateSafetyNote(
      clinicId,
      patientId,
      content
    );
  };

  /**
   * Get Code Status for a patient
   */
  const getCodeStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetCodeStatusResponse> => {
    return await visitNotesService.getCodeStatus(clinicId, patientId);
  };

  /**
   * Update Code Status for a patient
   */
  const updateCodeStatus = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<IUpdateCodeStatusResponse> => {
    return await visitNotesService.updateCodeStatus(
      clinicId,
      patientId,
      status,
      otherStatus
    );
  };

  return {
    createVisitNote,
    updateVisitNote,
    getAllVisitNotes,
    getVisitNote,
    summarizeNarrative,
    sendEfax,
    getSidePeekProfile,
    generateSidePeekProfile,
    updateSidePeekProfile,
    getSafetyNote,
    updateSafetyNote,
    getCodeStatus,
    updateCodeStatus,
  };
};

export type IVisitNotesControllerType = ReturnType<
  typeof VisitNotesControllerFactory
>;
